/*
 * @Description: umi-Request 请求封装
 */

// import 'nprogress/nprogress.css'; // 进度条样式

import {
  AxiosRequestConfig,
  request,
  RequestConfig,
  RequestError,
  RequestOptions,
} from '@umijs/max';
import { message, Modal } from 'antd';
import { debounce } from 'lodash-es'; // lodash-es 工具函数
// import Nprogress from 'nprogress';
import { getSimpleStatusMessage } from '@http-status-codes/i18n-zh';

import { getLocalStorageItem, isSuccess, logoutToLogin } from '@/utils'; // 全局工具函数
import { BASEURL, LOCAL_STORAGE, REQUEST_CODE, ROUTES } from '@/utils/enums';
import type { Response } from '@/utils/types';
import { reportAPI } from '@/ali-logger';
import { checkRules } from './patch';
import { NO_MESSAGE } from '@/constants/common';
import { getToken } from '../common/authToken';
import { isRequireAddToken } from './whiteList';
import { requestCache } from './requestCache';
// Nprogress.configure({ showSpinner: false });

// 创建一个 Set 来存储所有正在执行的请求控制器
const activeControllers = new Set<AbortController>();

type ErrorResponse = {
  data: ErrorResponseData;
  status: number;
};

type ErrorResponseData = {
  status_code: number;
  message: string;
  errors: Record<string, string>;
};

const throw422 = (errorResponse: ErrorResponse) => {
  const { data: errorData, status } = errorResponse || {};
  const errors = errorData.errors;
  const keys = Object.keys(errors);
  let errorList: any[] = [];
  keys.forEach((item) => {
    errorList = errorList.concat(errors[item]);
  });
  const reg = /^\d+\.\w+$/;
  let isBatch = true;
  if (!reg.test(keys?.[0] || '')) {
    errorList.forEach((item) => message.warning(item));
    isBatch = false;
  }

  throw { ...errorData, httpStatusCode: status, errorList, isBatch };
};

/**
 * @description: 防抖函数统一处理异常错误
 */
const debounceError = debounce(
  (content: string, duration = 10, type: 'error' | 'warning' = 'error') => {
    // NOTE 特殊情况不需要弹出消息
    if (content === NO_MESSAGE) {
      return;
    }
    message.open({
      type: type || 'error',
      content,
      duration,
      key: content,
    });
  },
  300,
);

/**
 * @description: 运行时配置，封装统一请求
 * @doc https://umijs.org/docs/max/request
 */
const umiRequest: RequestConfig = {
  baseURL: BASEURL.API, // 请求前缀
  timeout: 30 * 1000, // 超时时间，默认 30 s
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res: Response) => {
      const { code, message } = res;
      if (!isSuccess(code)) {
        throw new Error(message); // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: RequestError, opts: RequestOptions) => {
      // 计算请求总时间，即使请求失败也要计算
      const endTime = new Date().getTime();

      // 获取报错的响应和请求信息
      const { response, resquest } = error as any;
      // 配置 skipErrorHandler 会跳过默认的错误处理，用于项目中部分特殊的接口
      if (opts?.skipErrorHandler) throw error;
      // Axios 的错误
      // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
      if (response) {
        // 计算请求总时间
        const startTime = error.config.metadata.startTime;
        error.config.metadata.endTime = endTime;
        error.config.metadata.duration = endTime - startTime;

        reportAPI({
          url: error.config.url,
          success: false,
          time: error.config.metadata.duration,
          code: error.code,
          message: error.message,
        });

        const { data = {} } = response;
        const code = data.code || data.status_code || response.status;
        switch (code) {
          // token令牌校验，如果出现这个返回码则退出登录到登录页面
          case REQUEST_CODE.UNAUTHORIZED:
            {
              if (location.pathname === ROUTES.LOGIN) {
                logoutToLogin();
                message.warning(data.message);
                return;
              }

              // 这里加一个防抖
              Modal.warn({
                title: '登录已失效,请重新登录!',
                content: data.message,
                onOk: () => {
                  // 退出登录返回到登录页
                  logoutToLogin();
                  Modal.destroyAll();
                },
              });
            }
            break;
          case REQUEST_CODE.UNPROCESSABLEENTITY: {
            throw throw422(response as ErrorResponse);
          }
          case REQUEST_CODE.TOOMANYREQUESTS:
            {
              debounceError('请求过于频繁，请稍后再试！', 10, 'warning');
            }
            break;
          case REQUEST_CODE.REQUESTENTITYTOOLARGE:
            {
              debounceError('请求内容过于庞大，暂无法支持！', 10, 'warning');
            }
            break;
          default: {
            let httpCode = data.code;
            if (!httpCode || httpCode > 600) {
              httpCode = data.status_code;
            }
            // http 原生 code 兜底
            if (!httpCode || httpCode > 600) {
              httpCode = response.status;
            }
            debounceError(
              httpCode ? getSimpleStatusMessage(httpCode) : data.message || '服务器内部发生错误！',
              10,
              'warning',
            );
          }
        }
      } else if (resquest) {
        // 计算请求总时间
        const startTime = error.config.metadata.startTime;
        error.config.metadata.endTime = endTime;
        error.config.metadata.duration = endTime - startTime;

        reportAPI({
          url: error.config.url,
          success: false,
          time: error.config.metadata.duration,
          code: error.code,
          message: error.message,
        });

        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        debounceError('请求未成功,请重新尝试！');
      } else {
        if (error && error?.code === REQUEST_CODE.PITTIMEOUT) {
          throw error;
        }
        // 发送请求时发生了错误，比如网络断开、请求被取消等
        if (error?.code === 'ERR_CANCELED') {
          return;
        }

        let { message = '' } = error;
        message = checkRules(error) || message || '请求出错,请重新尝试！';
        // 发送请求时出了点问题
        debounceError(message);
      }
    },
  },
  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      // 记录请求开始时间
      config.metadata = { startTime: new Date().getTime() };

      // 根据白名单来判断该请求是否必须携带token
      if (isRequireAddToken(config.url)) {
        // 获取 ACCESS_TOKEN
        const ACCESS_TOKEN = await getToken();
        // 判断是否登录存在token，有就请求头携带token
        if (ACCESS_TOKEN && config?.headers && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${ACCESS_TOKEN}`;
        }
      }

      // 进度条开始
      // Nprogress.start();
      return { ...config };
    },
  ],
  // 响应拦截器
  responseInterceptors: [
    [
      // 响应处理
      (response) => {
        // 计算请求总时间
        const endTime = new Date().getTime();
        const startTime = response.config.metadata.startTime;
        response.config.metadata.endTime = endTime;
        response.config.metadata.duration = endTime - startTime;

        // 拦截响应数据，进行个性化处理
        const { data, config } = response as Response<any>;
        // 如果设置了 skipResponseInterceptor，直接返回响应
        if (config?.skipResponseInterceptor) {
          // 进度条结束
          // Nprogress.done();
          return {
            data,
          };
        }
        // 根据返回状态码，统一处理，需要前端和后端沟通确认
        switch (data.code) {
          case REQUEST_CODE.PITTIMEOUT:
            throw data;
          // 成功发起请求并成功处理，一般用于数据库字段校验
          case REQUEST_CODE.NOSUCCESS:
            debounceError(JSON.stringify(data.message));
            break;
          // 成功发起请求，但是内部处理出现错误
          case REQUEST_CODE.BADREQUEST:
            {
              debounceError(JSON.stringify(data.message));
            }
            break;
          // 登录信息失效
          case REQUEST_CODE.UNAUTHORIZED:
            // 退出登录返回到登录页
            logoutToLogin();
            Modal.destroyAll();
            break;
        }
        // 进度条结束
        // Nprogress.done();

        if (!isSuccess(data.code)) {
          // 上报错误
          reportAPI({
            url: response.config.url,
            success: false,
            time: response.config.metadata.duration,
            code: response.status,
            message: response.statusText,
          });

          // NOTE 抛出自制的错误，更清晰的错误信息
          throw { message: data.message, url: response?.config?.url || '' };
        }

        // 慢查询上报
        if (response.config.metadata.duration > 5000) {
          reportAPI({
            url: response.config.url,
            success: true,
            time: response.config.metadata.duration,
            code: response.status,
            message: response.statusText,
          });
        }

        return data;
      },
      // 错误处理
      (error: RequestError) => {
        // 进度条结束
        // Nprogress.done();
        return Promise.reject(error);
      },
    ],
  ],
};

// 创建可取消的请求
function createCancelableRequest<T>(
  method: AxiosRequestConfig['method'],
  url: string,
  data?: object,
  config?: AxiosRequestConfig,
) {
  // 检查缓存
  const cached = requestCache.get<T>(url, method || 'GET', data);
  if (cached !== null) {
    // 如果有缓存，返回一个立即 resolve 的 Promise
    return {
      run: async () => cached,
      cancel: () => {}, // 缓存数据无需取消
    };
  }

  const controller = new AbortController();
  activeControllers.add(controller);

  const run = request<T>(url, {
    method,
    ...(method === 'GET' ? { params: data } : { data }),
    signal: controller.signal,
    ...config,
  })
    .then((response: T) => {
      // 请求成功，保存缓存
      requestCache.set(url, method || 'GET', response, data);
      return response;
    })
    .catch((error: any) => {
      // 请求失败，清除缓存
      requestCache.clear(url, method || 'GET', data);
      throw error;
    })
    .finally(() => {
      // 请求结束后移除
      activeControllers.delete(controller);
    });

  return { run: async () => await run, cancel: () => controller.abort() };
}

/**
 * @description: 导出封装的请求方法（已集成自动缓存）
 */
export const httpRequest = {
  async get<T = any>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
    const cached = requestCache.get<T>(url, 'GET', data);
    if (cached !== null) return cached;

    try {
      const response = await request<T>(url, { method: 'GET', params: data, ...config });
      requestCache.set(url, 'GET', response, data);
      return response;
    } catch (error) {
      requestCache.clear(url, 'GET', data);
      throw error;
    }
  },

  async post<T = any>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
    const cached = requestCache.get<T>(url, 'POST', data);
    if (cached !== null) return cached;

    try {
      const response = await request<T>(url, { method: 'POST', data, ...config });
      requestCache.set(url, 'POST', response, data);
      return response;
    } catch (error) {
      requestCache.clear(url, 'POST', data);
      throw error;
    }
  },

  async put<T = any>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
    const cached = requestCache.get<T>(url, 'PUT', data);
    if (cached !== null) return cached;

    try {
      const response = await request<T>(url, { method: 'PUT', data, ...config });
      requestCache.set(url, 'PUT', response, data);
      return response;
    } catch (error) {
      requestCache.clear(url, 'PUT', data);
      throw error;
    }
  },

  async delete<T = any>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
    const cached = requestCache.get<T>(url, 'DELETE', data);
    if (cached !== null) return cached;

    try {
      const response = await request<T>(url, { method: 'DELETE', data, ...config });
      requestCache.set(url, 'DELETE', response, data);
      return response;
    } catch (error) {
      requestCache.clear(url, 'DELETE', data);
      throw error;
    }
  },

  async patch<T = any>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
    const cached = requestCache.get<T>(url, 'PATCH', data);
    if (cached !== null) return cached;

    try {
      const response = await request<T>(url, { method: 'PATCH', data, ...config });
      requestCache.set(url, 'PATCH', response, data);
      return response;
    } catch (error) {
      requestCache.clear(url, 'PATCH', data);
      throw error;
    }
  },

  // 可取消版本的 restful API
  cancelable: {
    get: <T = any>(url: string, data?: object, config?: AxiosRequestConfig) =>
      createCancelableRequest<T>('GET', url, data, config),
    post: <T = any>(url: string, data?: object, config?: AxiosRequestConfig) =>
      createCancelableRequest<T>('POST', url, data, config),
    put: <T = any>(url: string, data?: object, config?: AxiosRequestConfig) =>
      createCancelableRequest<T>('PUT', url, data, config),
    delete: <T = any>(url: string, data?: object, config?: AxiosRequestConfig) =>
      createCancelableRequest<T>('DELETE', url, data, config),
    patch: <T = any>(url: string, data?: object, config?: AxiosRequestConfig) =>
      createCancelableRequest<T>('PATCH', url, data, config),

    /**
     * @description: 创建一个可取消的 GET 请求，其它的类似
     * const { run, cancel } = httpRequest.cancelable.get('/api/user', { id: 1 });
     * const data = await run(); // 等待请求完成
     *
     * NOTE: 如果需要取消请求，可以调用 cancel 方法
     * cancel();
     *
     */
  },

  // 全局取消方法
  cancelAllRequests() {
    activeControllers.forEach((controller) => controller.abort());
    activeControllers.clear();
  },
  /**
   * @description: 取消所有正在进行的请求
   *
   * NOTE: 可以在需要的时候调用这个方法来取消所有的cancelable请求
   * httpRequest.cancelAllRequests();
   */

  /**
   * @description: 清除所有请求缓存
   *
   * NOTE: 所有请求都会自动根据 requestCache.ts 中的配置进行缓存
   * 如需清除缓存，可调用此方法
   * httpRequest.clearCache();
   */
  clearCache() {
    requestCache.clearAll();
  },
};

export default umiRequest;
